import { EnvironmentVariableEnum } from '../../main/constants/environment-variable.ts';
import { getAwsAuthUtil } from '../../main/utils/aws/aws-auth.util.ts';
import { getSentryUtil } from '../../main/utils/monitoring/sentry.util.ts';

import { mapMagicTokenResponseToAwsCognitoStorageDetails } from '../mappers/magic-token-response-to-aws-cognito-storage-details.mapper.ts';
import { magicTokenLoginRequest } from '../requests/magic-token-login.request.ts';
import { MagicLinkTokenResponse } from '../types/magic-link.types.ts';

interface LoginProps {
  magicToken: string;
  clearCurrentAuthCookies?: boolean;
}

export class PlatformMagicLinkUtil {
  private static instance: PlatformMagicLinkUtil;

  static getInstance(): PlatformMagicLinkUtil {
    if (!PlatformMagicLinkUtil.instance) {
      PlatformMagicLinkUtil.instance = new PlatformMagicLinkUtil(EnvironmentVariableEnum.PLATFORM_MAGIC_LINK_URL);
    }

    return PlatformMagicLinkUtil.instance;
  }

  constructor(
    private readonly magicLinkUrl: string,
    private readonly awsAuthUtil = getAwsAuthUtil(),
    private readonly sentryUtil = getSentryUtil()
  ) {}

  async login({ magicToken, clearCurrentAuthCookies }: LoginProps): Promise<MagicLinkTokenResponse | undefined> {
    try {
      // Conditionally do this because it might already cleared-out
      if (clearCurrentAuthCookies) {
        await this.awsAuthUtil.signOut();
      }

      const response = await magicTokenLoginRequest(this.magicLinkUrl, magicToken);
      if (!response) {
        return;
      }

      const authStorage = this.awsAuthUtil.getStorage();
      if (!authStorage) {
        return;
      }

      const mappedTokenDetails = mapMagicTokenResponseToAwsCognitoStorageDetails(response);

      const promises: Promise<void>[] = [];
      for (const key of Object.keys(mappedTokenDetails)) {
        promises.push(authStorage.setItem(key, mappedTokenDetails[key]));
      }
      await Promise.all(promises);

      return response;
    } catch (e) {
      this.sentryUtil.trackError(e as Error, { magicToken });
      return;
    }
  }
}

export const getPlatformMagicLinkUtil = (): PlatformMagicLinkUtil => PlatformMagicLinkUtil.getInstance();
